/**
 * V2 Node Edit Drawer — generic resource trigger model with backward-compatible
 * V1 event/filter auto-population.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Divider,
  Drawer,
  Group,
  Loader,
  MultiSelect,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { IconEye, IconPlus, IconTrash } from '@tabler/icons-react';

import type {
  ActionType,
  ConditionField,
  ConditionGroup,
  ConditionOperator,
  RecipientPolicy,
  ResourceFieldFilter,
  RuleAction,
  RuleCondition,
  RulePackDraft,
  ScheduledTimingDirection,
  ScheduledTimingUnit,
  TimingBucket,
  TriggerOperation,
  TriggerResourceType,
  WorkflowBranch,
} from '../../types';
import {
  ACTION_TYPES,
  CLAIM_MODIFIER_SYSTEM,
  CLAIM_POS_SYSTEM,
  CLAIM_WRITE_FIELDS,
  CONDITION_OPERATORS,
  EMAIL_RECIPIENT_POLICIES,
  EPISODE_OF_CARE_TYPE_OPTIONS,
  FORM_ASSIGNMENT_RECIPIENT_POLICIES,
  GENERIC_TO_V1_EVENT,
  NOTIFICATION_RECIPIENT_POLICIES,
  RESOURCE_CATEGORY_LABELS,
  RESOURCE_TYPE_REGISTRY,
  RULES_ENGINE_CODE_SYSTEM,
  SCHEDULED_TIMING_DIRECTIONS,
  SCHEDULED_TIMING_UNITS,
  SERVICE_REQUEST_CATEGORY_CODE_LEGACY,
  SERVICE_REQUEST_CATEGORY_SYSTEM_LEGACY,
  SERVICE_REQUEST_NEW_EPISODE_CODE,
  SERVICE_REQUEST_TYPE_SYSTEM,
  TASK_CODE_OPTIONS,
  TIMING_BUCKETS,
  TRIGGER_OPERATIONS,
  allowedActionTypes,
  buildConditionFieldGroupedData,
  getClaimWriteField,
  graphTokensForClaimField,
  isClaimAssemblyDomain,
  getScheduledTimingFieldsForResource,
  getTriggerFilterFields,
} from '../../types';
import { ClaimFhirReferenceSelect } from '../ClaimFhirReferenceSelect';
import { ConditionValueInput } from '../ConditionValueInput';
import { FilterMultiSelect, type FilterMultiSelectSection } from '@/components/FilterMultiSelect';
import { conditionValueAfterFieldChange } from '../../utils';
import { makeEditorId } from '../../utils';
import { useTemplateLibraryOptions } from '../../hooks/useTemplateLibraryOptions';
import { isClientShareRestrictedTemplate } from '@/lib/clientShareRestrictedTemplates';
import { useNotificationTemplates } from '../../../notifications/hooks/useNotificationTemplates';
import {
  NOTIFICATION_TEMPLATE_CONFIG_EXTENSION,
} from '../../../notifications/notification-template.constants';
import { getJsonExtension } from '../../../notifications/notification-template.medplum';
import type { NotificationTemplateConfig } from '../../../notifications/notification-template.types';
import { EmailTemplatePreviewDrawer } from '../../../notifications/components/EmailTemplatePreviewDrawer';
import {
  applyActionTypeChange,
  findActionById,
  findBranchById,
  getWorkflowSteps,
  setWorkflowSteps,
  updateActionById,
  updateBranchById,
} from './workflowTree';

export type DrawerTarget =
  | { kind: 'trigger' }
  | { kind: 'branch'; branchId: string }
  | { kind: 'action'; actionId: string }
  | null;

interface Props {
  target: DrawerTarget;
  draft: RulePackDraft;
  onUpdate: (partial: Partial<RulePackDraft>) => void;
  onClose: () => void;
}

export function NodeEditDrawer({
  target,
  draft,
  onUpdate,
  onClose,
}: Props): JSX.Element {
  if (!target) return <></>;

  return (
    <Drawer
      opened={!!target}
      onClose={onClose}
      title={getDrawerTitle(target)}
      position="right"
      size="md"
      padding="lg"
      overlayProps={{ backgroundOpacity: 0.15 }}
    >
      {target.kind === 'trigger' && (
        <TriggerEditor draft={draft} onUpdate={onUpdate} />
      )}
      {target.kind === 'branch' && (
        <BranchEditor
          draft={draft}
          branchId={target.branchId}
          onUpdate={onUpdate}
        />
      )}
      {target.kind === 'action' && (
        <ActionEditor
          draft={draft}
          actionId={target.actionId}
          onUpdate={onUpdate}
        />
      )}
    </Drawer>
  );
}

function getDrawerTitle(target: DrawerTarget): string {
  if (!target) return '';
  switch (target.kind) {
    case 'trigger':
      return 'Edit Trigger';
    case 'branch':
      return 'Edit branch';
    case 'action':
      return 'Edit Action';
  }
}

/* ═══════════════════════════════════════════════════════════ */
/*  V1 backward compat helpers                                */
/* ═══════════════════════════════════════════════════════════ */

function v1EventKindForGeneric(
  resourceType: TriggerResourceType,
  operation: TriggerOperation,
): string | undefined {
  return GENERIC_TO_V1_EVENT[`${resourceType}|${operation}`];
}

function v1ObjectFiltersFromFieldFilters(
  filters: ResourceFieldFilter[],
): RulePackDraft['trigger']['objectFilters'] {
  return filters
    .map((f) => {
      const kindMap: Record<string, string> = {
        questionnaire: f.value?.startsWith('http') ? 'questionnaire-canonical' : 'questionnaire-id',
        'code.coding': 'service-request-code',
        'category.coding': 'service-request-category',
        'episode.type': 'episode-type',
        'task.code': 'task-code',
      };
      const kind = kindMap[f.fieldPath];
      if (!kind) return null;
      return {
        id: f.id,
        kind: kind as ResourceFieldFilter['operator'] extends string ? never : never,
        system: f.system,
        code: f.value,
        value: f.value,
        label: f.label,
      } as unknown as RulePackDraft['trigger']['objectFilters'][number];
    })
    .filter(Boolean) as RulePackDraft['trigger']['objectFilters'];
}

/* ═══════════════════════════════════════════════════════════ */
/*  Resource Type Selector data                               */
/* ═══════════════════════════════════════════════════════════ */

function buildResourceTypeSelectData() {
  const byCategory = new Map<string, { value: string; label: string }[]>();
  for (const entry of RESOURCE_TYPE_REGISTRY) {
    const groupLabel = RESOURCE_CATEGORY_LABELS[entry.category] ?? entry.category;
    const list = byCategory.get(groupLabel) ?? [];
    list.push({ value: entry.resourceType, label: entry.label });
    byCategory.set(groupLabel, list);
  }
  return Array.from(byCategory.entries()).map(([group, items]) => ({ group, items }));
}

/* ═══════════════════════════════════════════════════════════ */
/*  Trigger Editor                                            */
/* ═══════════════════════════════════════════════════════════ */

function TriggerEditor({
  draft,
  onUpdate,
}: {
  draft: RulePackDraft;
  onUpdate: (partial: Partial<RulePackDraft>) => void;
}): JSX.Element {
  const [localTrigger, setLocalTrigger] = useState(draft.trigger);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    setLocalTrigger(draft.trigger);
    setIsDirty(false);
  }, [draft.trigger]);

  const selectedResource = localTrigger.resourceTypes[0] ?? null;
  const selectedOperations = localTrigger.operations?.length
    ? localTrigger.operations
    : (localTrigger.operation ? [localTrigger.operation] : []);

  const registryEntry = selectedResource
    ? RESOURCE_TYPE_REGISTRY.find((r) => r.resourceType === selectedResource)
    : undefined;

  const operationSelectData = registryEntry
    ? TRIGGER_OPERATIONS.filter((op) =>
        registryEntry.supportedOperations.includes(op.value),
      ).map((op) => ({ value: op.value, label: op.label, description: op.description }))
    : [];

  const updateLocal = (partial: Partial<RulePackDraft['trigger']>) => {
    setLocalTrigger((prev) => ({ ...prev, ...partial }));
    setIsDirty(true);
  };

  const handleSave = () => {
    const v1ObjFilters = v1ObjectFiltersFromFieldFilters(localTrigger.filters ?? []);
    onUpdate({ trigger: { ...localTrigger, objectFilters: v1ObjFilters } });
    setIsDirty(false);
  };

  const handleResourceChange = (value: string | null) => {
    const rt = (value as TriggerResourceType) ?? null;
    const entry = rt ? RESOURCE_TYPE_REGISTRY.find((r) => r.resourceType === rt) : undefined;
    const ops = entry?.supportedOperations ?? [];
    const defaultOp = ops.length === 1 ? ops[0] : (ops.includes('created') ? 'created' : ops[0]);
    const v1Event = rt && defaultOp ? v1EventKindForGeneric(rt, defaultOp) : undefined;
    updateLocal({
      resourceTypes: rt ? [rt] : [],
      operations: defaultOp ? [defaultOp] : [],
      operation: defaultOp,
      filters: [],
      objectFilters: [],
      eventKinds: v1Event ? [v1Event as RulePackDraft['trigger']['eventKinds'][number]] : [],
    });
  };

  const handleOperationsChange = (values: string[]) => {
    const operations = values as TriggerOperation[];
    const operation = operations.length === 1 ? operations[0] : undefined;
    const v1Event = selectedResource && operation ? v1EventKindForGeneric(selectedResource, operation) : undefined;
    updateLocal({
      operations,
      operation,
      eventKinds: v1Event ? [v1Event as RulePackDraft['trigger']['eventKinds'][number]] : [],
    });
  };

  return (
    <Stack gap="md">
      <Select
        label="Resource type"
        placeholder="Select a resource type..."
        data={buildResourceTypeSelectData()}
        value={selectedResource}
        onChange={handleResourceChange}
        searchable
      />

      <MultiSelect
        label="Operations"
        placeholder={selectedResource ? 'Select one or more operations...' : 'Select resource type first'}
        data={operationSelectData}
        value={selectedOperations}
        onChange={handleOperationsChange}
        disabled={!selectedResource}
      />

      <Divider />

      <TriggerFilterSection
        localTrigger={localTrigger}
        updateLocal={updateLocal}
      />

      <Button
        onClick={handleSave}
        disabled={!isDirty}
        color="gold"
      >
        Save trigger
      </Button>
    </Stack>
  );
}

/* ═══════════════════════════════════════════════════════════ */
/*  Trigger Filter Section (V2)                               */
/* ═══════════════════════════════════════════════════════════ */

function TriggerFilterSection({
  localTrigger,
  updateLocal,
}: {
  localTrigger: RulePackDraft['trigger'];
  updateLocal: (partial: Partial<RulePackDraft['trigger']>) => void;
}): JSX.Element {
  const filters = localTrigger.filters ?? [];
  const selectedResource = localTrigger.resourceTypes[0] ?? null;
  const { loading: tplLoading, error: tplError, formTemplateOptions, allTemplateOptions, getTemplateRef } = useTemplateLibraryOptions();

  const availableFields = selectedResource
    ? getTriggerFilterFields(selectedResource)
    : [];

  if (!selectedResource || availableFields.length === 0) return <></>;

  const fieldSelectData = availableFields.map((f) => ({
    value: f.fieldPath,
    label: f.label,
    description: f.description,
  }));

  const updateFilters = (newFilters: ResourceFieldFilter[]) => {
    updateLocal({ filters: newFilters });
  };

  const addFilter = () => {
    const defaultField = availableFields[0];
    const defaultOperator = defaultField.operators[0] ?? 'equals';
    updateFilters([
      ...filters,
      {
        id: makeEditorId('filter'),
        fieldPath: defaultField.fieldPath,
        operator: defaultOperator,
        value: '',
      },
    ]);
  };

  const updateFilter = (id: string, partial: Partial<ResourceFieldFilter>) => {
    updateFilters(filters.map((f) => (f.id === id ? { ...f, ...partial } : f)));
  };

  const removeFilter = (id: string) => {
    updateFilters(filters.filter((f) => f.id !== id));
  };

  const getFieldDef = (fieldPath: string) =>
    availableFields.find((f) => f.fieldPath === fieldPath);

  const handleFieldChange = (filterId: string, newFieldPath: string | null) => {
    if (!newFieldPath) return;
    const fieldDef = getFieldDef(newFieldPath);
    const defaultOp = fieldDef?.operators[0] ?? 'equals';
    updateFilter(filterId, { fieldPath: newFieldPath, operator: defaultOp, value: '', system: undefined, label: undefined });
  };

  const handleQuestionnaireSelect = (filterId: string, questionnaireId: string | null) => {
    if (!questionnaireId) {
      updateFilter(filterId, { value: '', label: '' });
      return;
    }
    const ref = getTemplateRef(questionnaireId);
    if (!ref) return;
    if (ref.canonicalUrl) {
      updateFilter(filterId, {
        value: ref.canonicalUrl,
        label: ref.title,
      });
    } else {
      updateFilter(filterId, {
        value: ref.id,
        label: ref.title,
      });
    }
  };

  const questionnaireSelectData = (formTemplateOptions.length > 0 ? formTemplateOptions : allTemplateOptions)
    .map((opt) => ({ value: opt.value, label: opt.label }));

  const SERVICE_REQUEST_CODE_OPTIONS = [
    { value: SERVICE_REQUEST_NEW_EPISODE_CODE, label: 'New Episode of Care request (canonical code)', system: SERVICE_REQUEST_TYPE_SYSTEM },
  ];

  const SERVICE_REQUEST_CATEGORY_OPTIONS = [
    { value: SERVICE_REQUEST_CATEGORY_CODE_LEGACY, label: 'Service request category (legacy)', system: SERVICE_REQUEST_CATEGORY_SYSTEM_LEGACY },
  ];

  const renderFilterValueInput = (filter: ResourceFieldFilter) => {
    const fieldDef = getFieldDef(filter.fieldPath);

    if (filter.fieldPath === 'questionnaire') {
      return (
        <>
          {tplLoading && <Text size="xs" c="dimmed">Loading questionnaires...</Text>}
          {tplError && <Text size="xs" c="red">Error: {tplError}</Text>}
          <Select
            size="xs"
            style={{ flex: 1, minWidth: 200 }}
            label="Questionnaire"
            placeholder="Select intake questionnaire..."
            data={questionnaireSelectData}
            value={
              questionnaireSelectData.some((d) => d.value === (filter.value ?? ''))
                ? (filter.value ?? null)
                : null
            }
            onChange={(v) => handleQuestionnaireSelect(filter.id, v)}
            searchable
            clearable
            disabled={tplLoading}
          />
          {filter.value && !questionnaireSelectData.some((d) => d.value === filter.value) && (
            <TextInput
              size="xs"
              style={{ flex: 1, minWidth: 200 }}
              label="Canonical URL / ID (manual)"
              value={filter.value ?? ''}
              onChange={(e) => updateFilter(filter.id, { value: e.currentTarget.value })}
            />
          )}
        </>
      );
    }

    if (filter.fieldPath === 'episode.type') {
      return (
        <Select
          size="xs"
          style={{ flex: 1, minWidth: 200 }}
          label="Episode type"
          placeholder="Select episode type..."
          data={EPISODE_OF_CARE_TYPE_OPTIONS}
          value={filter.value ?? null}
          onChange={(v) => {
            const opt = EPISODE_OF_CARE_TYPE_OPTIONS.find((o) => o.value === v);
            updateFilter(filter.id, {
              value: v ?? '',
              label: opt?.label ?? '',
            });
          }}
        />
      );
    }

    if (filter.fieldPath === 'code.coding') {
      return (
        <>
          <Select
            size="xs"
            style={{ flex: 1, minWidth: 200 }}
            label="Service request code"
            placeholder="Select code..."
            data={SERVICE_REQUEST_CODE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            value={filter.value ?? null}
            onChange={(v) => {
              const opt = SERVICE_REQUEST_CODE_OPTIONS.find((o) => o.value === v);
              updateFilter(filter.id, {
                system: opt?.system ?? SERVICE_REQUEST_TYPE_SYSTEM,
                value: v ?? '',
                label: opt?.label ?? '',
              });
            }}
          />
          <TextInput
            size="xs"
            w={250}
            label="System"
            value={filter.system ?? SERVICE_REQUEST_TYPE_SYSTEM}
            onChange={(e) => updateFilter(filter.id, { system: e.currentTarget.value })}
          />
        </>
      );
    }

    if (filter.fieldPath === 'category.coding') {
      return (
        <>
          <Select
            size="xs"
            style={{ flex: 1, minWidth: 200 }}
            label="Service request category"
            placeholder="Select category..."
            data={SERVICE_REQUEST_CATEGORY_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            value={filter.value ?? null}
            onChange={(v) => {
              const opt = SERVICE_REQUEST_CATEGORY_OPTIONS.find((o) => o.value === v);
              updateFilter(filter.id, {
                system: opt?.system ?? SERVICE_REQUEST_CATEGORY_SYSTEM_LEGACY,
                value: v ?? '',
                label: opt?.label ?? '',
              });
            }}
          />
          <TextInput
            size="xs"
            w={250}
            label="System"
            value={filter.system ?? SERVICE_REQUEST_CATEGORY_SYSTEM_LEGACY}
            onChange={(e) => updateFilter(filter.id, { system: e.currentTarget.value })}
          />
        </>
      );
    }

    if (filter.fieldPath === 'task.code') {
      return (
        <Select
          size="xs"
          style={{ flex: 1, minWidth: 200 }}
          label="Task code"
          placeholder="Select task code..."
          data={TASK_CODE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          value={filter.value ?? null}
          onChange={(v) => {
            const opt = TASK_CODE_OPTIONS.find((o) => o.value === v);
            updateFilter(filter.id, {
              system: opt?.system ?? RULES_ENGINE_CODE_SYSTEM,
              value: v ?? '',
              label: opt?.label ?? '',
            });
          }}
        />
      );
    }

    // Enum options from field definition
    if (fieldDef?.enumOptions && fieldDef.enumOptions.length > 0) {
      return (
        <Select
          size="xs"
          style={{ flex: 1, minWidth: 200 }}
          label="Value"
          placeholder="Select value..."
          data={fieldDef.enumOptions}
          value={filter.value ?? null}
          onChange={(v) => {
            const opt = fieldDef.enumOptions?.find((o) => o.value === v);
            updateFilter(filter.id, {
              value: v ?? '',
              label: opt?.label ?? '',
            });
          }}
        />
      );
    }

    // Generic text inputs for coding-type fields
    if (fieldDef?.valueType === 'coding') {
      return (
        <>
          <TextInput
            size="xs"
            w={250}
            label="System"
            placeholder="Code system URL"
            value={filter.system ?? ''}
            onChange={(e) => updateFilter(filter.id, { system: e.currentTarget.value })}
          />
          <TextInput
            size="xs"
            w={180}
            label="Code"
            placeholder="Code value"
            value={filter.value ?? ''}
            onChange={(e) => updateFilter(filter.id, { value: e.currentTarget.value })}
          />
        </>
      );
    }

    return (
      <TextInput
        size="xs"
        style={{ flex: 1, minWidth: 150 }}
        label="Value"
        placeholder="Filter value..."
        value={filter.value ?? ''}
        onChange={(e) => updateFilter(filter.id, { value: e.currentTarget.value })}
      />
    );
  };

  const renderOperatorSelect = (filter: ResourceFieldFilter) => {
    const fieldDef = getFieldDef(filter.fieldPath);
    const validOps = fieldDef?.operators ?? ['equals', 'not-equals'];
    const opData = CONDITION_OPERATORS
      .filter((o) => validOps.includes(o.value))
      .map((o) => ({ value: o.value, label: o.label }));

    return (
      <Select
        size="xs"
        w={140}
        label="Operator"
        data={opData}
        value={filter.operator}
        onChange={(v) =>
          updateFilter(filter.id, { operator: (v as ConditionOperator) ?? filter.operator })
        }
      />
    );
  };

  return (
    <>
      <Divider label="Trigger filters" labelPosition="left" />

      {filters.length === 0 && (
        <Text size="sm" c="dimmed">
          No filters. The trigger will fire for all {selectedResource} resources matching the operation.
        </Text>
      )}

      {filters.map((filter) => (
        <Group key={filter.id} gap="xs" wrap="wrap" align="flex-end">
          <Select
            size="xs"
            w={200}
            label="Field"
            data={fieldSelectData}
            value={filter.fieldPath}
            onChange={(v) => handleFieldChange(filter.id, v)}
            searchable
          />
          {renderOperatorSelect(filter)}
          {renderFilterValueInput(filter)}
          <ActionIcon
            variant="subtle"
            color="red"
            size="sm"
            onClick={() => removeFilter(filter.id)}
          >
            <IconTrash size={14} />
          </ActionIcon>
        </Group>
      ))}

      <Button
        variant="subtle"
        color="gold"
        size="sm"
        leftSection={<IconPlus size={14} />}
        onClick={addFilter}
        w="fit-content"
      >
        Add filter
      </Button>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════ */
/*  Rung / Branch Editor                                      */
/* ═══════════════════════════════════════════════════════════ */

function BranchEditor({
  draft,
  branchId,
  onUpdate,
}: {
  draft: RulePackDraft;
  branchId: string;
  onUpdate: (partial: Partial<RulePackDraft>) => void;
}): JSX.Element {
  const steps = getWorkflowSteps(draft);
  const branch = findBranchById(steps, branchId);

  // Fallback: try legacy flat rungs if tree search fails
  const legacyRung = !branch ? draft.rungs.find((r) => r.id === branchId) : undefined;
  const branchData: WorkflowBranch | undefined = branch ?? (legacyRung
    ? { id: legacyRung.id, name: legacyRung.name, description: legacyRung.description, enabled: legacyRung.enabled, alwaysRun: legacyRung.alwaysRun, conditionGroups: legacyRung.conditionGroups, steps: [], priority: legacyRung.priority }
    : undefined);

  if (!branchData) return <Text c="red">Branch not found</Text>;

  const conditionFieldData = buildConditionFieldGroupedData(draft.trigger.resourceTypes);

  const doUpdate = (partial: Partial<WorkflowBranch>) => {
    if (branch) {
      onUpdate(setWorkflowSteps(updateBranchById(steps, branchId, partial)));
    } else {
      onUpdate({
        rungs: draft.rungs.map((r) =>
          r.id === branchId ? { ...r, ...partial } : r,
        ),
      });
    }
  };

  const updateConditionGroup = (groupId: string, cg: ConditionGroup) => {
    doUpdate({
      conditionGroups: branchData.conditionGroups.map((g) =>
        g.id === groupId ? cg : g,
      ),
    });
  };

  const addConditionGroup = () => {
    doUpdate({
      conditionGroups: [
        ...branchData.conditionGroups,
        {
          id: makeEditorId('cg'),
          groupOperator: 'AND',
          conditions: [
            {
              id: makeEditorId('c'),
              field: 'patient.active' as ConditionField,
              operator: 'equals',
              value: '',
            },
          ],
        },
      ],
    });
  };

  const removeConditionGroup = (groupId: string) => {
    doUpdate({
      conditionGroups: branchData.conditionGroups.filter((g) => g.id !== groupId),
    });
  };

  return (
    <Stack gap="md">
      <TextInput
        label="Branch name"
        placeholder="e.g. After intake completion"
        value={branchData.name}
        onChange={(e) => doUpdate({ name: e.currentTarget.value })}
      />
      <Textarea
        label="Description"
        placeholder="What does this branch check?"
        value={branchData.description ?? ''}
        onChange={(e) => doUpdate({ description: e.currentTarget.value })}
        minRows={2}
      />

      <Divider label="Conditions" labelPosition="left" />

      {branchData.conditionGroups.length > 1 && (
        <Select
          size="xs"
          label="Combine condition groups"
          data={[
            { value: 'AND', label: 'All groups must match (AND)' },
            { value: 'OR', label: 'Any group can match (OR)' },
          ]}
          value={branchData.conditionGroupOperator ?? 'AND'}
          onChange={(v) => doUpdate({ conditionGroupOperator: (v as 'AND' | 'OR') ?? 'AND' })}
        />
      )}

      {branchData.conditionGroups.map((group, gi) => (
        <ConditionGroupPanel
          key={group.id}
          group={group}
          index={gi}
          canRemove={branchData.conditionGroups.length > 1}
          conditionFieldData={conditionFieldData}
          onUpdate={(g) => updateConditionGroup(group.id, g)}
          onRemove={() => removeConditionGroup(group.id)}
        />
      ))}

      <Button
        variant="subtle"
        color="gold"
        size="sm"
        leftSection={<IconPlus size={14} />}
        onClick={addConditionGroup}
        w="fit-content"
      >
        Add condition group
      </Button>
    </Stack>
  );
}

function ConditionGroupPanel({
  group,
  index,
  canRemove,
  conditionFieldData,
  onUpdate,
  onRemove,
}: {
  group: ConditionGroup;
  index: number;
  canRemove: boolean;
  conditionFieldData: { group: string; items: { value: string; label: string }[] }[];
  onUpdate: (g: ConditionGroup) => void;
  onRemove: () => void;
}): JSX.Element {
  const { formTemplateOptions } = useTemplateLibraryOptions();

  const addCondition = () => {
    onUpdate({
      ...group,
      conditions: [
        ...group.conditions,
        {
          id: makeEditorId('c'),
          field: 'patient.active' as ConditionField,
          operator: 'equals',
          value: '',
        },
      ],
    });
  };

  const updateCondition = (condId: string, cond: RuleCondition) => {
    onUpdate({
      ...group,
      conditions: group.conditions.map((c) =>
        c.id === condId ? cond : c,
      ),
    });
  };

  const removeCondition = (condId: string) => {
    onUpdate({
      ...group,
      conditions: group.conditions.filter((c) => c.id !== condId),
    });
  };

  return (
    <Stack
      gap="xs"
      p="sm"
      style={{
        border: '1px solid var(--mantine-color-gray-3)',
        borderRadius: 'var(--mantine-radius-md)',
        background: 'var(--mantine-color-gray-0)',
      }}
    >
      <Group justify="space-between" gap="xs">
        <Group gap="xs">
          <Badge size="sm" variant="light" color="gray">
            Group {index + 1}
          </Badge>
          <Select
            size="xs"
            w={80}
            data={[
              { value: 'AND', label: 'AND' },
              { value: 'OR', label: 'OR' },
            ]}
            value={group.groupOperator}
            onChange={(v) =>
              onUpdate({ ...group, groupOperator: (v as 'AND' | 'OR') ?? 'AND' })
            }
          />
        </Group>
        {canRemove && (
          <ActionIcon
            variant="subtle"
            color="red"
            size="xs"
            onClick={onRemove}
          >
            <IconTrash size={12} />
          </ActionIcon>
        )}
      </Group>

      {group.conditions.map((cond, ci) => (
        <div key={cond.id}>
          {ci > 0 && (
            <Text size="xs" c="dimmed" ta="center" my={2}>
              {group.groupOperator}
            </Text>
          )}
          <Group gap="xs" wrap="wrap">
            <Select
              size="xs"
              w={200}
              placeholder="Field"
              searchable
              data={conditionFieldData}
              value={cond.field}
              onChange={(v) => {
                const newField = (v as ConditionField) ?? cond.field;
                const nextValue = conditionValueAfterFieldChange(
                  newField,
                  cond.operator,
                  cond.value,
                );
                updateCondition(cond.id, {
                  ...cond,
                  field: newField,
                  value: nextValue,
                });
              }}
            />
            <Select
              size="xs"
              w={150}
              placeholder="Operator"
              data={CONDITION_OPERATORS.map((o) => ({
                value: o.value,
                label: o.label,
              }))}
              value={cond.operator}
              onChange={(v) =>
                updateCondition(cond.id, {
                  ...cond,
                  operator: (v as ConditionOperator) ?? cond.operator,
                })
              }
            />
            {cond.field === 'derived.incompleteFormTemplateIds' ? (
              <MultiSelect
                size="xs"
                placeholder="Select required forms..."
                data={formTemplateOptions.map((o) => ({ value: o.value, label: o.label }))}
                value={cond.value ? cond.value.split(',').filter(Boolean) : []}
                onChange={(values) => updateCondition(cond.id, { ...cond, value: values.join(',') })}
                searchable
                style={{ flex: 1, minWidth: 200 }}
              />
            ) : (
              <ConditionValueInput
                field={cond.field}
                operator={cond.operator}
                value={cond.value}
                onChange={(next) => updateCondition(cond.id, { ...cond, value: next })}
                style={{ flex: 1, minWidth: 100 }}
              />
            )}
            <ActionIcon
              variant="subtle"
              color="red"
              size="xs"
              onClick={() => removeCondition(cond.id)}
              disabled={group.conditions.length <= 1}
            >
              <IconTrash size={12} />
            </ActionIcon>
          </Group>
        </div>
      ))}

      <Button
        variant="subtle"
        color="gray"
        size="xs"
        leftSection={<IconPlus size={12} />}
        onClick={addCondition}
        w="fit-content"
      >
        Add condition
      </Button>
    </Stack>
  );
}

/* ═══════════════════════════════════════════════════════════ */
/*  V2 Action Editor                                          */
/* ═══════════════════════════════════════════════════════════ */

function ActionEditor({
  draft,
  actionId,
  onUpdate,
}: {
  draft: RulePackDraft;
  actionId: string;
  onUpdate: (partial: Partial<RulePackDraft>) => void;
}): JSX.Element {
  const steps = getWorkflowSteps(draft);
  const action = findActionById(steps, actionId);

  // Fallback: search legacy flat rungs
  const legacyAction = !action
    ? draft.rungs.flatMap((r) => r.actions).find((a) => a.id === actionId)
    : undefined;
  const actionData = action ?? legacyAction;
  const {
    loading: templateLoading,
    error: templateError,
    templateFilterSections,
    allTemplateOptions,
    getTemplateRef,
  } = useTemplateLibraryOptions();

  if (!actionData) return <Text c="red">Action not found</Text>;

  const updateAction = (partial: Partial<RuleAction>) => {
    if (action) {
      onUpdate(setWorkflowSteps(updateActionById(steps, actionId, partial)));
    } else {
      onUpdate({
        rungs: draft.rungs.map((r) => ({
          ...r,
          actions: r.actions.map((a) =>
            a.id === actionId ? { ...a, ...partial } : a,
          ),
        })),
      });
    }
  };

  return (
    <Stack gap="md">
      <Select
        label="Action type"
        description="What kind of action should this perform?"
        data={allowedActionTypes(draft.domain).map((t) => {
          const meta = ACTION_TYPES.find((a) => a.value === t);
          return { value: t, label: meta?.label ?? t, description: meta?.description };
        })}
        value={actionData.type}
        onChange={(v) => {
          const newType = (v as ActionType) ?? actionData.type;
          if (newType === actionData.type) return;
          updateAction(applyActionTypeChange(actionData, newType));
        }}
      />

      <TextInput
        label="Label"
        placeholder="Short description of this action"
        value={actionData.label}
        onChange={(e) => updateAction({ label: e.currentTarget.value })}
      />

      {templateLoading && (
        <Text size="sm" c="dimmed">Loading templates...</Text>
      )}
      {templateError && (
        <Text size="sm" c="red">Template loading error: {templateError}</Text>
      )}

      {actionData.type === 'send-notification' && (
        <NotificationActionFields
          action={actionData}
          onUpdate={updateAction}
        />
      )}

      {actionData.type === 'send-email' && (
        <EmailActionFields
          action={actionData}
          onUpdate={updateAction}
        />
      )}

      {actionData.type === 'assign-template-forms' && (
        <FormAssignmentActionFields
          action={actionData}
          templateFilterSections={templateFilterSections}
          allTemplateOptions={allTemplateOptions}
          getTemplateRef={getTemplateRef}
          templateLoading={templateLoading}
          templateError={templateError}
          onUpdate={updateAction}
        />
      )}

      {actionData.type === 'set-claim-field' && (
        <SetClaimFieldActionFields
          action={actionData}
          onUpdate={updateAction}
        />
      )}

      {/* A form assignment is always immediate: delayed form assignment is not offered (ENG-3764 D29). */}
      {!isClaimAssemblyDomain(draft.domain) && actionData.type !== 'assign-template-forms' && (
        <>
          <Divider />
          <ActionTimingFields
            action={actionData}
            triggerResourceTypes={draft.trigger.resourceTypes}
            onUpdate={updateAction}
          />
        </>
      )}

      <Divider />

      <Textarea
        label="Notes (optional)"
        placeholder="Internal notes about this action"
        value={actionData.description ?? ''}
        onChange={(e) =>
          updateAction({ description: e.currentTarget.value })
        }
        minRows={2}
      />
    </Stack>
  );
}

/* ═══════════════════════════════════════════════════════════ */
/*  Action timing fields                                      */
/* ═══════════════════════════════════════════════════════════ */

function SetClaimFieldActionFields({
  action,
  onUpdate,
}: {
  action: RuleAction;
  onUpdate: (partial: Partial<RuleAction>) => void;
}): JSX.Element {
  const writeField = action.claimFieldPath ? getClaimWriteField(action.claimFieldPath) : undefined;
  const graphTokens = action.claimFieldPath ? graphTokensForClaimField(action.claimFieldPath) : [];
  const valueSource = action.claimFieldValueSource === 'graph' ? 'graph' : 'literal';
  const enumOptions = writeField?.enumOptions;
  const codingSystem = writeField?.codingSystem;
  const showSystem =
    valueSource === 'literal' &&
    (writeField?.valueType === 'coding' || writeField?.valueType === 'code' || writeField?.valueType === 'reference') &&
    !codingSystem;

  const onFieldChange = (v: string | null): void => {
    const next = getClaimWriteField(v ?? '');
    onUpdate({
      claimFieldPath: v ?? undefined,
      claimFieldValue: '',
      claimFieldSystem: next?.codingSystem,
      claimFieldValueSource: undefined,
    });
  };

  return (
    <Stack gap="sm">
      <Select
        label="Claim field"
        description="Field to set on the Claim draft"
        data={CLAIM_WRITE_FIELDS.map((f) => ({ value: f.fieldPath, label: f.label }))}
        value={action.claimFieldPath ?? null}
        onChange={onFieldChange}
        searchable
      />
      {graphTokens.length > 0 && (
        <Select
          label="Value kind"
          data={[
            { value: 'literal', label: 'Hardcoded value' },
            { value: 'graph', label: 'From claim graph' },
          ]}
          value={valueSource}
          onChange={(v) => {
            const graph = v === 'graph';
            onUpdate({
              claimFieldValueSource: graph ? 'graph' : undefined,
              claimFieldValue: '',
              claimFieldSystem: graph ? undefined : writeField?.codingSystem,
            });
          }}
        />
      )}
      {valueSource === 'graph' ? (
        <Select
          label="Graph value"
          description="Stores a Practitioner or Location reference token, not an NPI string"
          data={graphTokens.map((t) => ({ value: t.token, label: t.label }))}
          value={action.claimFieldValue ?? null}
          onChange={(v) => onUpdate({ claimFieldValue: v ?? '', claimFieldValueSource: 'graph' })}
        />
      ) : writeField?.referenceResourceType ? (
        <ClaimFhirReferenceSelect
          resourceType={writeField.referenceResourceType}
          value={action.claimFieldValue ?? ''}
          persistFullReference
          onChange={(v) => onUpdate({ claimFieldValue: v, claimFieldValueSource: undefined })}
        />
      ) : writeField?.multi && enumOptions && enumOptions.length > 0 ? (
        <MultiSelect
          label="Value"
          data={enumOptions}
          value={(action.claimFieldValue ?? '').split(',').map((s) => s.trim()).filter(Boolean)}
          onChange={(codes) =>
            onUpdate({
              claimFieldValue: codes.join(','),
              claimFieldSystem: codingSystem ?? action.claimFieldSystem,
              claimFieldValueSource: undefined,
            })
          }
          searchable
        />
      ) : enumOptions && enumOptions.length > 0 ? (
        <Select
          label="Value"
          data={enumOptions}
          value={action.claimFieldValue ?? null}
          onChange={(v) =>
            onUpdate({
              claimFieldValue: v ?? '',
              claimFieldSystem: codingSystem ?? action.claimFieldSystem,
              claimFieldValueSource: undefined,
            })
          }
          searchable
        />
      ) : (
        <TextInput
          label="Value"
          description={writeField?.description ?? 'Value to write onto the Claim'}
          value={action.claimFieldValue ?? ''}
          onChange={(e) => onUpdate({ claimFieldValue: e.currentTarget.value, claimFieldValueSource: undefined })}
        />
      )}
      {showSystem && (
        <TextInput
          label="Code system"
          description="Required for CPT/HCPCS and similar codings"
          placeholder="e.g. http://www.ama-assn.org/go/cpt"
          value={action.claimFieldSystem ?? ''}
          onChange={(e) => {
            const next = e.currentTarget.value.trim();
            onUpdate({ claimFieldSystem: next || undefined });
          }}
        />
      )}
      {valueSource === 'literal' && codingSystem && (
        <Text size="xs" c="dimmed">
          Code system: {codingSystem === CLAIM_POS_SYSTEM ? 'CMS POS' : codingSystem === CLAIM_MODIFIER_SYSTEM ? 'CPT modifiers' : codingSystem}
        </Text>
      )}
    </Stack>
  );
}

function ActionTimingFields({
  action,
  triggerResourceTypes,
  onUpdate,
}: {
  action: RuleAction;
  triggerResourceTypes: TriggerResourceType[];
  onUpdate: (partial: Partial<RuleAction>) => void;
}): JSX.Element {
  const timingMode = action.timingBucket ?? 'immediate';
  const timing = action.scheduledTiming;
  const referenceFieldOptions = triggerResourceTypes.flatMap((rt) =>
    getScheduledTimingFieldsForResource(rt).map((f) => ({
      value: f.fieldPath,
      label: `${f.label} (${f.fieldPath})`,
    })),
  );

  return (
    <Stack gap="sm">
      <Select
        label="Timing"
        description="When should this action execute?"
        data={TIMING_BUCKETS.map((t) => ({ value: t.value, label: t.label }))}
        value={timingMode}
        onChange={(v) => {
          const bucket = (v as TimingBucket) ?? 'immediate';
          if (bucket === 'immediate') {
            onUpdate({ timingBucket: 'immediate', scheduledTiming: undefined });
          } else {
            onUpdate({
              timingBucket: 'scheduled',
              scheduledTiming: timing ?? { direction: 'before', offsetAmount: 24, offsetUnit: 'hours', referenceField: referenceFieldOptions[0]?.value ?? '' },
            });
          }
        }}
      />

      {timingMode === 'scheduled' && timing && (
        <>
          <Group grow>
            <Select
              label="Direction"
              data={SCHEDULED_TIMING_DIRECTIONS.map((d) => ({ value: d.value, label: d.label }))}
              value={timing.direction}
              onChange={(v) =>
                onUpdate({
                  scheduledTiming: {
                    ...timing,
                    direction: (v as ScheduledTimingDirection) ?? timing.direction,
                    offsetAmount: v === 'at' ? 0 : timing.offsetAmount || 1,
                  },
                })
              }
            />
            <Select
              label="Reference field"
              description="Datetime field on the trigger resource"
              data={referenceFieldOptions}
              value={timing.referenceField}
              onChange={(v) =>
                onUpdate({ scheduledTiming: { ...timing, referenceField: v ?? '' } })
              }
              searchable
            />
          </Group>

          {timing.direction !== 'at' && (
            <Group grow>
              <NumberInput
                label="Offset"
                min={1}
                max={999}
                value={timing.offsetAmount}
                onChange={(v) =>
                  onUpdate({ scheduledTiming: { ...timing, offsetAmount: typeof v === 'number' ? v : 1 } })
                }
              />
              <Select
                label="Unit"
                data={SCHEDULED_TIMING_UNITS.map((u) => ({ value: u.value, label: u.label }))}
                value={timing.offsetUnit}
                onChange={(v) =>
                  onUpdate({ scheduledTiming: { ...timing, offsetUnit: (v as ScheduledTimingUnit) ?? timing.offsetUnit } })
                }
              />
            </Group>
          )}
        </>
      )}
    </Stack>
  );
}

/* ═══════════════════════════════════════════════════════════ */
/*  Notification action fields                                */
/* ═══════════════════════════════════════════════════════════ */

function NotificationActionFields({
  action,
  onUpdate,
}: {
  action: RuleAction;
  onUpdate: (partial: Partial<RuleAction>) => void;
}): JSX.Element {
  const recipientPolicies = action.notificationRecipientPolicies ?? [];

  return (
    <>
      <Divider label="Notification content" labelPosition="left" />

      <TextInput
        label="Notification title"
        description="The title shown in the in-app notification. Falls back to action label if empty."
        placeholder="e.g. New form assigned"
        value={action.notificationTitle ?? ''}
        onChange={(e) => onUpdate({ notificationTitle: e.currentTarget.value })}
      />

      <Textarea
        label="Notification body"
        description="The body text of the notification. Falls back to action description if empty."
        placeholder="e.g. Please complete your intake questionnaire."
        value={action.notificationBody ?? ''}
        onChange={(e) => onUpdate({ notificationBody: e.currentTarget.value })}
        minRows={2}
      />

      <Divider label="Recipients" labelPosition="left" />

      <MultiSelect
        label="Notification recipients"
        description="Who should receive this in-app notification?"
        data={NOTIFICATION_RECIPIENT_POLICIES.map((r) => ({
          value: r.value,
          label: r.label,
        }))}
        value={recipientPolicies}
        onChange={(values) =>
          onUpdate({ notificationRecipientPolicies: values as RecipientPolicy[] })
        }
        placeholder="Select recipients..."
      />
    </>
  );
}

/* ═══════════════════════════════════════════════════════════ */
/*  Email action fields                                       */
/* ═══════════════════════════════════════════════════════════ */

function EmailActionFields({
  action,
  onUpdate,
}: {
  action: RuleAction;
  onUpdate: (partial: Partial<RuleAction>) => void;
}): JSX.Element {
  const { templates, loading: ntLoading } = useNotificationTemplates();
  const recipientPolicies = action.emailRecipientPolicies ?? [];
  const [previewOpened, setPreviewOpened] = useState(false);

  const templateSelectData = templates
    .filter((ad) => ad.status === 'active')
    .map((ad) => {
      const config = getJsonExtension<NotificationTemplateConfig>(
        ad,
        NOTIFICATION_TEMPLATE_CONFIG_EXTENSION,
      );
      return {
        value: ad.id ?? '',
        label: config?.name ?? ad.name ?? ad.title ?? 'Untitled template',
        version: config?.version ?? ad.version ?? '1',
      };
    })
    .filter((d) => d.value);

  const selectedTemplate = templates.find((ad) => ad.id === action.emailTemplateRef?.id) ?? null;

  return (
    <>
      <Divider label="Email template" labelPosition="left" />

      {ntLoading ? (
        <Group gap="xs"><Loader size="xs" /><Text size="sm" c="dimmed">Loading email templates...</Text></Group>
      ) : templateSelectData.length === 0 ? (
        <Text size="sm" c="orange">
          No active email templates found. Create one in Settings → Email Notifications first.
        </Text>
      ) : (
        <Group gap="xs" align="flex-end">
          <Select
            label="Email template"
            description="Select a template from Email Notifications"
            data={templateSelectData}
            value={action.emailTemplateRef?.id ?? null}
            onChange={(v) => {
              if (!v) {
                onUpdate({ emailTemplateRef: undefined });
                return;
              }
              const opt = templateSelectData.find((d) => d.value === v);
              onUpdate({
                emailTemplateRef: {
                  id: v,
                  title: opt?.label ?? v,
                  version: opt?.version,
                },
              });
            }}
            placeholder="Select email template..."
            searchable
            clearable
            style={{ flex: 1 }}
          />
          <ActionIcon
            variant="default"
            size="input-md"
            aria-label="Preview email template"
            disabled={!selectedTemplate}
            onClick={() => setPreviewOpened(true)}
          >
            <IconEye size={18} />
          </ActionIcon>
        </Group>
      )}

      {action.emailTemplateRef?.id && (
        <Text size="xs" c="dimmed">
          Template: {action.emailTemplateRef.title}
        </Text>
      )}

      <EmailTemplatePreviewDrawer
        opened={previewOpened}
        onClose={() => setPreviewOpened(false)}
        template={selectedTemplate}
      />

      <Divider label="Recipients" labelPosition="left" />

      <MultiSelect
        label="Email recipients"
        description="Who should receive this email?"
        data={EMAIL_RECIPIENT_POLICIES.map((r) => ({
          value: r.value,
          label: r.label,
        }))}
        value={recipientPolicies}
        onChange={(values) =>
          onUpdate({ emailRecipientPolicies: values as RecipientPolicy[] })
        }
        placeholder="Select recipients..."
      />
    </>
  );
}

/* ═══════════════════════════════════════════════════════════ */
/*  Form assignment action fields                             */
/* ═══════════════════════════════════════════════════════════ */

function FormAssignmentActionFields({
  action,
  templateFilterSections,
  allTemplateOptions,
  getTemplateRef,
  templateLoading,
  templateError,
  onUpdate,
}: {
  action: RuleAction;
  templateFilterSections: FilterMultiSelectSection[];
  allTemplateOptions: { value: string; label: string }[];
  getTemplateRef: (id: string) => { id: string; title: string; category?: string; type?: string; canonicalUrl?: string; version?: string } | undefined;
  templateLoading: boolean;
  templateError: string | null;
  onUpdate: (partial: Partial<RuleAction>) => void;
}): JSX.Element {
  const selectedFormIds = (action.formTemplateRefs ?? [])
    .filter((ref) => !isClientShareRestrictedTemplate(ref))
    .map((f) => f.id);

  const sections = useMemo(() => {
    const known = new Set(allTemplateOptions.map((o) => o.value));
    const orphanRefs = (action.formTemplateRefs ?? []).filter(
      (r) => !known.has(r.id) && !isClientShareRestrictedTemplate(r),
    );
    if (orphanRefs.length === 0) return templateFilterSections;
    return [
      ...templateFilterSections,
      {
        label: 'Saved selections',
        data: orphanRefs.map((r) => ({ value: r.id, label: r.title })),
      },
    ];
  }, [templateFilterSections, allTemplateOptions, action.formTemplateRefs]);

  const resolveTemplateRef = (id: string) =>
    getTemplateRef(id) ?? action.formTemplateRefs?.find((r) => r.id === id);

  return (
    <>
      <Divider label="Forms / templates to assign" labelPosition="left" />

      {templateLoading && (
        <Group gap="xs">
          <Loader size="xs" />
          <Text size="sm" c="dimmed">Loading templates from Template Library...</Text>
        </Group>
      )}
      {templateError && !templateLoading && sections.length === 0 && (
        <Text size="sm" c="red">{templateError}</Text>
      )}

      <FilterMultiSelect
        label="Forms / templates"
        allLabel="All forms"
        sections={sections}
        data={[]}
        value={selectedFormIds}
        onChange={(values) => {
          const refs = values
            .map((v) => resolveTemplateRef(v))
            .filter((r): r is NonNullable<typeof r> => !!r)
            .filter((r) => !isClientShareRestrictedTemplate(r));
          onUpdate({ formTemplateRefs: refs });
        }}
        emptyMeansAll={false}
        hideAllOption
        clearable
        disabled={templateLoading}
        placeholder="Search and select forms..."
        selectedLabel="selected"
      />
      <Text size="xs" c="dimmed">
        Select one or more forms from the Template Library. The latest version of each template is used automatically.
      </Text>

      <Divider label="Assignment recipients" labelPosition="left" />

      <MultiSelect
        label="Assign to"
        description="V1 supports assigning forms to the client only"
        data={FORM_ASSIGNMENT_RECIPIENT_POLICIES.map((r) => ({
          value: r.value,
          label: r.label,
        }))}
        value={action.assignmentRecipientPolicies ?? []}
        onChange={(values) =>
          onUpdate({ assignmentRecipientPolicies: values as RecipientPolicy[] })
        }
        placeholder="Select assignment target..."
      />

      <NumberInput
        label="Due in (days)"
        description="How many days after assignment should forms be due?"
        value={action.assignmentDueOffsetDays ?? ''}
        onChange={(v) =>
          onUpdate({ assignmentDueOffsetDays: typeof v === 'number' ? v : undefined })
        }
        min={0}
        max={365}
        placeholder="e.g. 7"
      />
    </>
  );
}
